/**
 * useAiAnalysisBridge：DashboardLayout -> AiAnalysis 的“数据桥接”层。
 *
 * 这个 Hook 只做两件事：
 * - 判断当前布局中是否存在 AI 分析卡片（用于决定是否需要拉取 promptOptions 等额外资源；列表无跨会话缓存，每次进入带 AI 卡片的报表会请求）；
 * - 把 DashboardLayout 已有的过滤条件与布局信息转换成 AiAnalysis 所需的 queries/scopeTabs/taskIds。
 *
 * 约束/注意点：
 * - scopeTabs 的 charts 只应该包含“可分析的图表卡片”（排除 text/AI 卡片本身），否则会出现“分析范围里包含自己”或无 task_id 的异常。
 * - taskIds 用 Set 去重，避免同一个图表在多 Tab/多布局来源下重复出现导致请求参数膨胀。
 */
import { useMemo } from 'react';

import type { IAiScopeTabItem } from '../types';
import { AI_ANALYSIS_TYPE } from '../../../_config';
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import type { IUserFilterConfig } from '@dbl/core/dashboard';
import type { TLayoutAiAnalysisType, TLayoutChartType, TLayoutTextType } from '../../../hooks';

export interface IAiAnalysisBridgeParams {
  layouts: (TLayoutChartType | TLayoutTextType | TLayoutAiAnalysisType)[];
  tabLayouts?: Array<{
    tabId: string;
    tabName: string;
    tabNameCN?: string;
    layouts: Array<{
      i?: string;
      type?: string;
      chartType?: string;
      task_id?: string;
      name?: string;
      name_cn?: string;
    }>;
  }>;
  filterValues: IFilterValueRequest[] | null;
  userFilterConfig: IUserFilterConfig | null;
}

export interface IAiAnalysisBridgeResult {
  hasAiCard: boolean;
  aiQueries: {
    filters?: IFilterValueRequest[] | null;
    user_filter?: IUserFilterConfig | null;
  };
  aiScopeTabs: IAiScopeTabItem[];
  aiScopeTaskIds: string[];
}

export const useAiAnalysisBridge = (params: IAiAnalysisBridgeParams): IAiAnalysisBridgeResult => {
  const { layouts, tabLayouts, filterValues, userFilterConfig } = params;

  // ===== 1) 布局层能力探测：是否存在 AI 卡片 =====
  // 这个布尔值的主要用途不是展示，而是“是否值得去初始化 AI 分析相关资源”。
  // 例如没有 AI 卡片时，可避免请求 promptOptions；有 AI 卡片时每次挂载/启用都会请求列表。
  const hasAiCard = useMemo(
    () => layouts.some((item) => item?.type === AI_ANALYSIS_TYPE || item?.chartType === AI_ANALYSIS_TYPE),
    [layouts],
  );

  // ===== 2) 查询条件桥接：保持字段名与后端 queries 一致 =====
  // 这里不做字段裁剪或重命名，原因是 runAiAnalysis 最终会把整个 queries 原样透传给后端，
  // 本层只负责把 DashboardLayout 侧的 filterValues/userFilterConfig 组装成 AI 模块识别的结构。
  const aiQueries = useMemo(
    () => ({
      filters: filterValues,
      user_filter: userFilterConfig,
    }),
    [filterValues, userFilterConfig],
  );

  // ===== 3) 分析范围：把 layout/tabLayouts 转为 “tab -> charts(taskId)” 结构 =====
  const aiScopeTabs = useMemo<IAiScopeTabItem[]>(() => {
    if (!hasAiCard) {
      return [];
    }

    // 没有 tabLayouts 时，退化成“单 Tab 全报表”视图。
    // 这样 Settings 里的 scope 逻辑不需要区分“有 Tab/无 Tab”两套数据形态。
    const sourceTabs = tabLayouts?.length
      ? tabLayouts
      : [{
        tabId: '-1',
        tabName: '',
        tabNameCN: '',
        layouts,
      }];

    return sourceTabs.map((tabItem) => {
      // 用单次遍历替代 filter + map，减少中间数组分配，降低 tab/layout 较大时的遍历开销。
      const charts: IAiScopeTabItem['charts'] = [];
      for (const layoutItem of (tabItem.layouts || [])) {
        // 只保留真正可分析的图表：
        // - text 没有 task_id，无法作为分析输入；
        // - AI 卡片自己也不能进入分析范围，否则会出现“分析自己”的循环定义。
        if (
          layoutItem?.type === 'text'
          || layoutItem?.type === AI_ANALYSIS_TYPE
          || layoutItem?.chartType === AI_ANALYSIS_TYPE
        ) {
          continue;
        }
        if (!layoutItem?.task_id) {
          continue;
        }

        charts.push({
          // 此处保留 name / nameCn，是为了后续 scope 选择器或展示文案可以直接消费，不需要回 Layout 再查一次。
          chartId: String(layoutItem.task_id),
          name: layoutItem.name,
          nameCn: layoutItem.name_cn,
        });
      }

      return {
        tabId: String(tabItem.tabId || ''),
        tabName: String(tabItem.tabName || tabItem.tabId || ''),
        tabNameCN: String(tabItem.tabNameCN || tabItem.tabName || tabItem.tabId || ''),
        charts,
      };
    });
  }, [hasAiCard, tabLayouts]);

  // ===== 4) “全局范围”taskIds：用于默认全选/分析整个报表 =====
  const aiScopeTaskIds = useMemo(() => {
    if (!hasAiCard) {
      return [];
    }

    // 用 Set 去重，避免相同图表在多来源布局数据里重复出现。
    const taskIdSet = new Set<string>();
    for (const tabItem of aiScopeTabs) {
      for (const chartItem of tabItem.charts) {
        taskIdSet.add(chartItem.chartId);
      }
    }
    return Array.from(taskIdSet);
  }, [hasAiCard, aiScopeTabs]);

  return {
    hasAiCard,
    aiQueries,
    aiScopeTabs,
    aiScopeTaskIds,
  };
};

